/**
 * Platform Detection Utilities
 *
 * Uses Capacitor to detect whether the app is running inside a native
 * mobile shell (Android/iOS) or in a standard web browser.
 *
 * This is the single gate that controls which router tree is rendered:
 *   - Native platform  -> MobileRouter (5-6 screens)
 *   - Web browser       -> Full WebRouter (all routes)
 */
import { Capacitor } from '@capacitor/core';

/**
 * Returns true when running inside a Capacitor native shell (Android or iOS).
 * Returns false in a regular browser or during SSR.
 */
export const isNativePlatform = (): boolean => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/**
 * Returns the current platform: 'ios', 'android', or 'web'.
 */
export const getPlatform = (): 'ios' | 'android' | 'web' => {
  try {
    return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
  } catch {
    return 'web';
  }
};

/**
 * Returns true when running on iOS (native).
 */
export const isIOS = (): boolean => getPlatform() === 'ios';

/**
 * Returns true when running on Android (native).
 */
export const isAndroid = (): boolean => getPlatform() === 'android';

